import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DropdownMenu, IconButton } from 'components/ds'
import Tooltip from 'components/ui/tooltip'

import { qrOptions } from './qr-options'
import QrCodeSvg from 'assets/icons/qr.svg'
import Check from 'assets/icons/check.svg'
import Close from 'assets/icons/close.svg'

type QRCodeStylingInstance = {
  download: (options: { name: string }) => Promise<void>
  getRawData: (type: string) => Promise<Blob>
}

type QRCodeStylingConstructor = new (
  options: Record<string, unknown>,
) => QRCodeStylingInstance

type Props = {
  url: string
  filename?: string
  disabled?: boolean
  onAction?: (action: 'download' | 'copy') => void
}

const QRCodeButton = ({ url, filename, disabled, onAction }: Props) => {
  const [status, setStatus] = useState<'pending' | 'success' | 'fail'>(
    'pending',
  )
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current)
      }
    }
  }, [])

  const resetStatus = useCallback(() => {
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current)
    statusTimeoutRef.current = setTimeout(() => {
      setStatus('pending')
    }, 1000)
  }, [])

  const downloadQR = useCallback(async () => {
    try {
      const QRCodeStyling = (await import('qr-code-styling'))
        .default as unknown as QRCodeStylingConstructor
      const qr = new QRCodeStyling(qrOptions(url))
      await qr.download({ name: filename ? filename : 'qr' })
      setStatus('success')
      onAction?.('download')
    } catch {
      setStatus('fail')
    }
    resetStatus()
  }, [url, filename, onAction, resetStatus])

  const copyQR = useCallback(async () => {
    try {
      const QRCodeStyling = (await import('qr-code-styling'))
        .default as unknown as QRCodeStylingConstructor
      const qr = new QRCodeStyling(qrOptions(url))
      const blobPromise = qr.getRawData('png') as Promise<Blob>
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blobPromise }),
      ])
      setStatus('success')
      onAction?.('copy')
    } catch {
      setStatus('fail')
    }
    resetStatus()
  }, [url, onAction, resetStatus])

  const statusIcon =
    status === 'success' ? (
      <Tooltip content="Success">
        <span>
          <Check width="24" height="24" />
        </span>
      </Tooltip>
    ) : (
      <Tooltip content="Failed">
        <span>
          <Close width="24" height="24" />
        </span>
      </Tooltip>
    )

  return (
    <DropdownMenu.Root>
      <Tooltip content={status === 'pending' ? 'Copy QR Code' : undefined}>
        <DropdownMenu.Trigger
          disabled={disabled || !url || status !== 'pending'}
        >
          {status !== 'pending' ? (
            statusIcon
          ) : (
            <IconButton
              variant="soft"
              color="gray"
              aria-label="QR code actions"
            >
              <QrCodeSvg width="24" height="24" />
            </IconButton>
          )}
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Content>
        <DropdownMenu.Item onClick={downloadQR}>Download</DropdownMenu.Item>
        <DropdownMenu.Item onClick={copyQR}>Copy</DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default React.memo<Props>(QRCodeButton)
